import React, { useState } from "react";
import { Package, IndianRupee, TrendingUp, BarChart2 } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtAmt = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const STATUS_COLORS = {
  active: "bg-green-100 text-green-800 border-green-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  expired: "bg-red-100 text-red-800 border-red-200",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
  failed: "bg-gray-200 text-gray-700 border-gray-300",
};
const statusCls = (s) =>
  STATUS_COLORS[(s || "").toLowerCase()] || "bg-gray-100 text-gray-700 border-gray-200";

const Badge = ({ label, cls }) => (
  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
    {label}
  </span>
);

const StatCard = ({ icon: Icon, label, value, iconCls }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconCls}`}>
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-400 truncate">{label}</p>
      <p className="text-base font-bold text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

const DailyChart = ({ daily = [], mode }) => {
  if (!daily.length) {
    return <div className="h-32 flex items-center justify-center text-xs text-gray-400">No data</div>;
  }

  const CHART_HEIGHT = 128; // px — matches the h-32 wrapper below
  const MIN_BAR_HEIGHT = 3; // px — keep zero-value bars faintly visible

  const values = daily.map((d) => Number(d[mode] || 0));
  const max = Math.max(...values, 1);

  return (
    <div
      className="flex items-end gap-1.5 w-full"
      style={{ height: `${CHART_HEIGHT}px` }}
    >
      {daily.map((d, i) => {
        const val = Number(d[mode] || 0);
        const heightPx = Math.max(Math.round((val / max) * CHART_HEIGHT), MIN_BAR_HEIGHT);
        return (
          <div
            key={i}
            className="flex-1 flex flex-col items-center justify-end group relative"
            style={{ height: `${CHART_HEIGHT}px` }}
            title={`${fmtDate(d.date)}: ${mode === "revenue" ? fmtAmt(val) : fmt(val)}`}
          >
            <div
              className="w-full rounded-t bg-indigo-500 group-hover:bg-indigo-600 transition-all"
              style={{ height: `${heightPx}px` }}
            />
          </div>
        );
      })}
    </div>
  );
};

const StatsDashboard = ({ stats, loading }) => {
  const [chartMode, setChartMode] = useState("orders");

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-5">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }
  if (!stats) return null;

  const { total, statusBreakdown = [], daily = [], range } = stats;
  const activeCount = statusBreakdown.find((s) => s._id === "active")?.count || 0;
  const expiredCount = statusBreakdown.find((s) => s._id === "expired")?.count || 0;

  return (
    <div className="space-y-4 my-5">
      {range && (
        <p className="text-xs text-gray-400">
          Showing data from{" "}
          <span className="font-medium text-gray-600">{fmtDate(range.startDate)}</span> to{" "}
          <span className="font-medium text-gray-600">{fmtDate(range.endDate)}</span>
          {stats.provider_number && (
            <>
              {" "}for provider{" "}
              <span className="font-mono font-medium text-gray-600">{stats.provider_number}</span>
            </>
          )}
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard icon={Package} label="Total orders" value={fmt(total?.orders)} iconCls="bg-blue-50 text-blue-600" />
        <StatCard icon={IndianRupee} label="Total revenue" value={fmtAmt(total?.revenue)} iconCls="bg-green-50 text-green-600" />
        <StatCard icon={TrendingUp} label="Base Price Collected" value={fmtAmt(total?.totalBasePrice)} iconCls="bg-purple-50 text-purple-600" />
        <StatCard icon={IndianRupee} label="Total Gateway Fee Collected" value={fmtAmt(total?.totalPlatformGatewayFee)} iconCls="bg-green-50 text-green-600" />
        <StatCard icon={BarChart2} label="Total Platform Fee Collected" value={fmtAmt(total?.totalPlatformFlatFee)} iconCls="bg-amber-50 text-amber-600" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-600 mb-4">Status breakdown</h3>
          <div className="space-y-2.5">
            {statusBreakdown.map((item) => {
              const pct = total?.orders > 0 ? ((item.count / total.orders) * 100).toFixed(1) : 0;
              return (
                <div key={item._id}>
                  <div className="flex justify-between items-center mb-1">
                    <Badge label={item._id} cls={statusCls(item._id)} />
                    <span className="text-sm font-semibold text-gray-800">
                      {fmt(item.count)}{" "}
                      <span className="text-xs font-normal text-gray-400">({pct}%)</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${item._id === "active" ? "bg-green-400" : item._id === "expired" ? "bg-red-400" : "bg-gray-400"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-2 text-center">
            <div>
              <p className="text-2xl font-bold text-green-600">{fmt(activeCount)}</p>
              <p className="text-xs text-gray-400 mt-0.5">Active</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-500">{fmt(expiredCount)}</p>
              <p className="text-xs text-gray-400 mt-0.5">Expired</p>
            </div>
          </div>
        </div>

        <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600">Daily trend</h3>
            <div className="flex gap-1">
              {["orders", "revenue"].map((m) => (
                <button
                  key={m}
                  onClick={() => setChartMode(m)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition ${chartMode === m ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <DailyChart daily={daily} mode={chartMode} />

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400">
                  <th className="text-left py-1 pr-3 font-medium">Date</th>
                  <th className="text-right py-1 pr-3 font-medium">Orders</th>
                  <th className="text-right py-1 font-medium">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {[...daily].reverse().map((d, i) => (
                  <tr key={i} className="text-gray-700">
                    <td className="py-1 pr-3">{fmtDate(d.date)}</td>
                    <td className="py-1 pr-3 text-right font-medium">{d.orders}</td>
                    <td className="py-1 text-right font-medium text-green-700">{fmtAmt(d.revenue)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold border-t border-gray-200 text-gray-900">
                  <td className="py-1.5 pr-3 text-xs">Total</td>
                  <td className="py-1.5 pr-3 text-right text-xs">{fmt(total?.orders)}</td>
                  <td className="py-1.5 text-right text-xs text-green-700">{fmtAmt(total?.revenue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsDashboard;